import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { acceptCookies } from '../../actions/cookies';
import { CookiesAgreementModal } from '../../components/CookiesAgreementModal';
import { CookiesNotificationV2 } from '../../components/CookiesNotificationV2';
import { selectMobileBannerCookieState } from '../../selectors/cookies/selectMobileBannerCookieState';
import { selectOnboardingCookieState } from '../../selectors/cookies/selectOnboardingCookieState';
import { ICookiesState } from '../../types/cookies';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { useApplicationContext } from '../../utils/applicationContext';
import { useIsFullscreenBannerSkipped } from '../MobileAppFullscreenBannerContainer/useIsFullscreenBannerSkipped';
import { useIsOnboardingEnabled } from '../OnboardingContainer/useIsOnboardingEnabled';
import { useOnboardingData } from '../OnboardingContainer/useOnboardingData';

export const CookiesNotificationContainer: React.FC = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const { config } = useApplicationContext();
  const isOnboardingEnabled = useIsOnboardingEnabled();
  const isFullscreenBannerSkipped = useIsFullscreenBannerSkipped();
  const onboardingData = useOnboardingData();

  const [isAgreementOpen, setIsAgreementOpen] = React.useState(false);

  const { accepted } = useSelector<IApplicationState, ICookiesState>(state => state.cookies);
  const noResize = useSelector<IApplicationState, boolean>(state => state.noResize);
  const onboardingShown = useSelector(selectOnboardingCookieState);
  const mobileBannerShown = useSelector(selectMobileBannerCookieState);

  const isWebView = config.get('web.isWebView') || false;

  const isOnboardingDisabledOrHasBeenShown =
    deviceType !== 'phone' || !isOnboardingEnabled || !onboardingData || onboardingShown;

  const isMobileBannerDisabledOrHasBeenShown = deviceType !== 'phone' || isFullscreenBannerSkipped || mobileBannerShown;

  const isCookiesNotificationEnabled = React.useMemo(
    () => !isWebView && !accepted && !isAgreementOpen,
    [isWebView, accepted, isAgreementOpen],
  );

  const handleAgreementView = React.useCallback(() => {
    setIsAgreementOpen(true);
  }, []);

  const handleAccept = React.useCallback(() => {
    dispatch(acceptCookies());
    setIsAgreementOpen(false);
  }, [dispatch]);

  return (
    <>
      {isCookiesNotificationEnabled && (
        <CookiesNotificationV2
          noResize={noResize}
          isMobile={deviceType === 'phone'}
          onAgreementView={handleAgreementView}
          onClose={handleAccept}
          isTimerAvailable={isOnboardingDisabledOrHasBeenShown && isMobileBannerDisabledOrHasBeenShown}
        />
      )}
      <CookiesAgreementModal
        open={isAgreementOpen}
        phone={deviceType === 'phone'}
        onAccept={handleAccept}
        onOpenChange={setIsAgreementOpen}
        isRedesign={true}
      />
    </>
  );
};
